import React from 'react'

import { alpha, Box, Stack, Typography, useTheme } from '@mui/material'
import type { LucideIcon } from 'lucide-react'

import { Button, Modal } from '@repo/ui'
import type { ActivationTriggerValue } from '@repo/types'

import { ACTIVATION_TRIGGERS } from 'constants/activationTriggers'
import { SelectionDot } from 'components/display/SelectionDot'

import { ConfirmSettingModal } from '../ConfirmSettingModal'

/** `null` is a real choice here — "inherit from the level above" (AC-6). */
type Choice = ActivationTriggerValue | null

interface EditActivationTriggerModalProps {
  open: boolean
  onClose: () => void
  scope: 'product' | 'certificate'
  /** The record's current own override; `null` = currently inheriting. */
  current: Choice
  /** What it resolves to when inheriting, so the inherit row can name it. */
  inheritedTitle: string
  /**
   * The trigger `inheritedTitle` names, as an id — so the inherit row can carry the
   * SAME glyph as the option it resolves to.
   *
   * The id rather than the icon itself, because the icon is a property of the
   * trigger and lives on `ACTIVATION_TRIGGERS` (that constant's docstring is
   * explicit that no call site picks a glyph). Optional so the row degrades to an
   * empty 18px slot rather than a misaligned one if a future caller omits it; both
   * of today's call sites already resolve this value to build `inheritedTitle`, so
   * passing it costs them nothing.
   */
  inheritedTrigger?: ActivationTriggerValue
  loading?: boolean
  /**
   * `activateAfter` says the chosen rule names a moment already in the past, so
   * saving it alone would change nothing the user can see.
   *
   * The modal reports the INTENT and the caller owns the mutations, because the two
   * writes have to be SEQUENCED — save first, activate on its success. Firing both
   * from here ran them concurrently, and each one's success handler refetched: the
   * refetch that landed last won the cache, and it could be the one that read before
   * the other write committed. The record was right and the page showed it stale.
   */
  onSave: (next: Choice, opts?: {activateAfter?: boolean}) => void
  /**
   * VERA-931 — certificates only. True when the certificate is already live, so
   * NO automatic rule can still fire for it. The setting is still worth recording
   * (it survives a future deactivation) but the modal must not imply it will do
   * something.
   */
  alreadyActivated?: boolean
  /**
   * VERA-931 — the certificate has already ARRIVED somewhere, but is not live.
   *
   * This is the case Spencer raised: you switch a certificate to "On location arrival"
   * after the arrival has happened. The rule is not wrong, it is simply overtaken —
   * nothing will fire it a second time — so the modal says so and offers the action
   * that CAN change the state today.
   *
   * Distinct from `alreadyActivated`, which is the opposite situation (the
   * certificate is live and no rule matters). Conflating them offers "activate now"
   * on a certificate that is already active.
   */
  alreadyArrived?: boolean
}

/**
 * VRG-528 — pick, then confirm, the activation-trigger override for one product or
 * one certificate.
 *
 * TWO SEQUENTIAL MODALS, NEVER TWO AT ONCE. Choosing is a different act from
 * accepting a billing consequence, and AC-11 requires the same
 * `ConfirmSettingModal` the company-level setting already uses. So the picker
 * CLOSES when Save is pressed and the confirm opens; cancelling the confirm
 * reopens the picker with the selection intact rather than throwing the choice
 * away. Stacking them would put a scrim over the options the reader is being asked
 * to accept.
 *
 * The gate fires BEFORE anything is applied (§43): nothing is sent until confirm,
 * so cancelling cannot leave a row showing a value that was never stored.
 */
export const EditActivationTriggerModal = ({
  open,
  onClose,
  scope,
  current,
  inheritedTitle,
  inheritedTrigger,
  loading,
  onSave,
  alreadyActivated = false,
  alreadyArrived = false,
}: EditActivationTriggerModalProps) => {
  const theme = useTheme()
  const [choice, setChoice] = React.useState<Choice>(current)
  const [pending, setPending] = React.useState<Choice | undefined>(undefined)

  /*
    Re-seed on open so the picker never shows the previous record's selection — and
    RESET on close, which is the half that was missing.

    `pending` is local state and `confirming` was derived from it alone, so the
    confirm's visibility did not depend on `open` at all. The parent closing the
    modal on a successful save (`setOpenActivation(false)`) therefore hid only the
    PICKER and left the confirm mounted and on screen — which reads exactly as
    "Confirm did nothing", and is what made Clear override look inert. Worse, the
    stranded confirm then re-rendered against freshly refetched props, so after an
    activate it silently swapped its own copy to the already-active variant: the
    same modal, apparently reopened by itself, saying something new.
  */
  React.useEffect(() => {
    setPending(undefined)
    if (open) setChoice(current)
  }, [open, current])

  // Gated on `open` as well, so the parent closing this component closes BOTH
  // modals. Deriving it from `pending` alone is the bug described above.
  const confirming = open && pending !== undefined
  const dirty = choice !== current

  /*
    The chosen rule names a moment that is already in the PAST for this certificate.
    Today that is exactly one case — picking "On location arrival" on an item that has
    already been received somewhere — and it is worth special-casing because the
    alternative is a setting that looks applied and then never does anything.

    Guarded on the certificate not already being live: if it is, no rule matters and
    the `alreadyActivated` notice covers it instead.
  */
  const momentAlreadyPassed =
    !alreadyActivated && alreadyArrived && pending === 'AT_LOCATION'

  const options: Array<{
    id: Choice
    title: string
    description: string
    /**
     * Always read from `ACTIVATION_TRIGGERS`, never chosen here — see that file.
     * `undefined` only when the inherit row was given no `inheritedTrigger`.
     */
    icon?: LucideIcon
    disabled?: boolean
  }> = [
    {
      id: null,
      // The inherit row borrows the glyph of the trigger it RESOLVES to, because
      // that is what this row will actually do. A glyph of its own would be a
      // fourth symbol for a thing that is not a fourth option.
      icon: ACTIVATION_TRIGGERS.find(option => option.id === inheritedTrigger)
        ?.icon,
      title: `Inherit — ${inheritedTitle}`,
      description:
        scope === 'certificate'
          ? 'Follow the product, and then the company default. Changing either will change this certificate.'
          : 'Follow the company default. Changing it will change this product.',
    },
    /*
      ON_CREATION IS NOT OFFERED ON A CERTIFICATE, and that is not a policy choice —
      it is arithmetic. This modal is only reachable from a certificate that already
      exists, so "activate at creation" names a moment that has already gone. Storing
      it would record a rule that can never fire, and the row would then report a
      trigger the certificate will never honour.

      What the operator wants in that moment is the Activate button, which is always
      present and always means "now" (AC-9). Product and company scope keep the
      option, because there the certificates have not been created yet.
    */
    ...ACTIVATION_TRIGGERS.filter(
      option => !(scope === 'certificate' && option.id === 'ON_CREATION'),
    ).map(option => ({
      id: option.id as ActivationTriggerValue,
      icon: option.icon,
      title: option.comingSoon ? `${option.title} — coming soon` : option.title,
      description: option.description,
      disabled: option.comingSoon,
    })),
  ]

  const chosenTitle =
    options.find(option => option.id === pending)?.title ?? inheritedTitle

  return (
    <>
      <Modal
        title={`Activation for this ${scope}`}
        open={open && !confirming}
        onClose={loading ? () => {} : onClose}
        actions={
          <Stack direction="row" spacing={3}>
            <Button variant="secondary" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            {/*
              §54 — dead until the form is dirty. An enabled Save on an untouched
              picker promises an action that would do nothing, and here it would
              also open a billing-consequence modal for a change that isn't one.
              Cancel stays enabled: it is the exit, and an exit always exists.
            */}
            <Button
              onClick={() => setPending(choice)}
              disabled={loading || !dirty}
            >
              Save
            </Button>
          </Stack>
        }
      >
        <Stack spacing={3}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ lineHeight: 1.5 }}
          >
            Choose the moment {scope === 'product' ? 'certificates created from this product go' : 'this certificate goes'} live.
            That moment is also when {scope === 'product' ? 'they are' : 'it is'} charged, so this
            changes when billing starts.
          </Typography>

          {/*
            NO already-active notice here, deliberately.

            It used to sit above the options, saying the same thing the confirm says
            one click later. The picker's job is to let you CHOOSE; the consequence
            of the choice belongs at the point where the choice is accepted, and
            saying it in both places made the first one look like an error message
            about the screen you were already on. Removed rather than reworded: the
            confirm carries it, and carrying it once is what makes it read.
          */}
          {options.map(option => {
            const selected = option.id === choice
            const Icon = option.icon
            return (
              <Box
                key={String(option.id)}
                role="radio"
                aria-checked={selected}
                aria-disabled={option.disabled}
                onClick={() => !option.disabled && setChoice(option.id)}
                sx={{
                  display: 'flex',
                  gap: 3,
                  p: 5,
                  borderRadius: 3,
                  cursor: option.disabled ? 'not-allowed' : 'pointer',
                  opacity: option.disabled ? 0.5 : 1,
                  // §34 selectable-card: tint = "considering", border = "chosen".
                  // Never a brand border on hover here — it would collide with the
                  // selected state and destroy the distinction.
                  border: `1px solid ${
                    selected
                      ? theme.palette.brand[500]
                      : theme.palette.primary[100]
                  }`,
                  backgroundColor: selected
                    ? alpha(theme.palette.brand[500], 0.1)
                    : 'transparent',
                  '&:hover': option.disabled
                    ? undefined
                    : {
                        backgroundColor: selected
                          ? alpha(theme.palette.brand[500], 0.1)
                          : alpha(theme.palette.brand[500], 0.06),
                      },
                }}
              >
                <Box sx={{ mt: '2px' }}>
                  <SelectionDot selected={selected} />
                </Box>
                {/*
                  The trigger's glyph, between the control and the text.

                  ALWAYS RENDERED, even when there is no icon — an 18px box either
                  way, so the inherit row cannot fall out of alignment with the
                  three below it if a caller omits `inheritedTrigger`. A conditional
                  element would collapse the column for that one row and shift its
                  text left of every other row's.

                  `mt` matches the dot beside it rather than `alignItems: center`,
                  so it sits on the FIRST line of a two-line block instead of
                  drifting down as the description wraps. Untinted: the selection is
                  already carried by the dot, the border and the fill, and a fourth
                  state-dependent signal would be noise.
                */}
                <Box
                  aria-hidden
                  sx={{
                    width: 18,
                    height: 18,
                    flexShrink: 0,
                    mt: '2px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: theme.palette.text.secondary,
                  }}
                >
                  {Icon && <Icon size={18} strokeWidth={1} />}
                </Box>
                <Stack spacing={1}>
                  <Typography variant="body2" color="text.primary">
                    {option.title}
                  </Typography>
                  <Typography
                    variant="body3"
                    color="text.secondary"
                    sx={{ lineHeight: 1.5 }}
                  >
                    {option.description}
                  </Typography>
                </Stack>
              </Box>
            )
          })}
        </Stack>
      </Modal>

      <ConfirmSettingModal
        open={confirming}
        // Cancelling the consequence returns to the picker with the choice intact.
        // Dropping back to the page would make the reader redo the selection to
        // read the consequence twice, which is how people learn to skip reading it.
        onClose={() => setPending(undefined)}
        /*
          DO NOT clear `pending` here. Clearing it unmounts the confirm on the same
          tick the request is fired, and because the picker's `open` is still true,
          the picker reappears underneath — which reads exactly as "the button did
          nothing and sent me back". Spencer reported it as an unclickable Confirm;
          the click was registering fine, the modal was just being yanked out from
          under it mid-flight.

          Instead the confirm stays mounted in its `loading` state and the PARENT
          closes the whole thing on success (`open={false}`), so the two states are
          "working" and "gone". On failure `loading` drops back to false and the
          confirm is still there with its button live, which is the correct place to
          be told a billing change did not happen.
        */
        // Save FIRST, then activate — the caller sequences the two writes (see
        // `onSave`). The rule is what the user chose and is worth recording either
        // way; the activation is the remedy for that rule having already been
        // overtaken. Firing only the second would silently discard the choice they
        // just made.
        onConfirm={() => onSave(pending as Choice, {activateAfter: momentAlreadyPassed})}
        // Subject/verb agreement differs by scope: "certificates activate" (plural)
        // vs "this certificate activates". The whole clause varies, not just a noun.
        title={
          scope === 'product'
            ? 'Change when this product’s certificates activate?'
            : 'Change when this certificate activates?'
        }
        consequence={
          pending === null
            ? `This ${scope} will follow ${scope === 'certificate' ? 'its product, then ' : ''}the company default (${inheritedTitle}). If that default changes later, this will change with it — including when charges start.`
            : `A certificate becomes billable when it activates, so this changes when charges start for ${scope === 'product' ? 'every certificate created from this product from now on' : 'this certificate'}. Certificates that have already activated are unaffected.`
        }
        /*
          When the certificate is already live the chosen rule cannot fire for it,
          so the honest primary action is the one that CAN change something today.
          The setting is still saved — it governs a later re-activation — but the
          button stops promising an effect it will not have.
        */
        confirmLabel={
          momentAlreadyPassed
            ? 'Save and activate now'
            : pending === null
              ? 'Clear override'
              : `Activate ${chosenTitle.toLowerCase()}`
        }
        loading={loading}
        /*
          EXACTLY ONE ALERT IN THIS MODAL, and it is the billing consequence.

          Both of these notices used to render as a second `Alert` stacked under it.
          Two amber boxes read as two warnings of equal weight, and the eye cannot
          tell which one is the thing being confirmed — the charge, which is the
          only irreversible part, lost its emphasis to a piece of context. As prose
          above it, they do the job they were actually for: explaining why the
          consequence reads the way it does.
        */
        preamble={
          momentAlreadyPassed
            ? 'This certificate has already arrived at a location, so switching to “On location arrival” will not activate it on its own. That moment has passed. Confirming saves the setting and activates it now.'
            : alreadyActivated && pending !== null
              ? 'This certificate is already active, so this rule will not fire for it now. Saving records it for a future re-activation.'
              : undefined
        }
      />
    </>
  )
}
